import * as Sentry from '@sentry/react';
import { type DragEvent, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useAuth } from '@pasqal/core/hooks/useAuth';
import { Footer } from '@pasqal/core/ui/apps/Footer';
import { Header } from '@pasqal/core/ui/apps/Header';
import { Sidebar } from '@pasqal/core/ui/apps/Sidebar';
import AlertContent from '~/components/AlertContent/AlertContent';
import ContextMenu from '~/components/ContextMenu/ContextMenu';
import ExperimentRunControl from '~/components/ExperimentRunControl/ExperimentRunControl';
import ExperimentSpecsControl from '~/components/ExperimentSpecsControl/ExperimentSpecsControl';
import ExperimentTabs from '~/components/ExperimentTabs/ExperimentTabs';
import HelpWindow from '~/components/HelpWindow/HelpWindow';
import LayoutRoot from '~/components/LayoutRoot/LayoutRoot';
import NewExperimentTab from '~/components/NewExperimentTab/NewExperimentTab';
import { SidebarContent } from '~/components/SidebarContent';
import { TitleIconButton } from '~/components/SidebarContent/SidebarContent';
import UndoControl from '~/components/UndoControl/UndoControl';

import { useFetchJob } from '~/hooks/useJobs';
import { useAlert } from '~/systems/alert';
import { CurrentFrameProvider } from '~/systems/currentFrame';
import {
  ExperimentProvider,
  useExperiment
} from '~/systems/experiment/ExperimentProvider';
import { SimulationProvider, useSimulation } from '~/systems/simulation';
import { UiStateProvider } from '~/systems/uiState';

import './ExperimentPage.css';

function ExperimentPageInner() {
  const { logout } = useAuth();

  const { addExperiment, experiments, isSyncingState, load, loadSaved } =
    useExperiment();
  const alertSys = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();

  const jobId = searchParams.get('job') ?? undefined;
  const { data: jobData } = useFetchJob(jobId);

  const [dragging, setDragging] = useState(0);
  const [isCreatingNewExperiment, setIsCreatingNewExperiment] = useState(false);

  const tryLoad = useCallback(
    async (text: string) => {
      try {
        const error = load(text);
        if (error) {
          Sentry.captureMessage(error, 'error');
          alertSys.setAlert(error);
        }
      } catch (e) {
        Sentry.captureException(e);
        alertSys.setAlert((e as Error).message);
      }
    },
    [alertSys, load]
  );

  const handleCreateNewExperiment = useCallback(
    async (fromSequence?: string) => {
      addExperiment();
      if (fromSequence) {
        await tryLoad(fromSequence);
      }
      setIsCreatingNewExperiment(false);
    },
    [addExperiment, tryLoad]
  );

  // Guarantee dragging state resets when drag ends or is cancelled (e.g. Escape key),
  // so pointer-events: none is never permanently stuck on GoldenLayout splitters
  useEffect(() => {
    const reset = () => setDragging(0);
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    return () => {
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', reset);
    };
  }, []);

  // Load saved state on mount
  useEffect(() => {
    loadSaved();
  }, [loadSaved]);

  // Watch for empty experiments
  useEffect(() => {
    if (experiments.length === 0) {
      setIsCreatingNewExperiment(true);
    }
  }, [experiments.length]);

  // Load job from URL
  useEffect(() => {
    if (!jobData) {
      return;
    }

    const errorMessage = `Unknown sequence for job id: ${jobData.id}`;
    if (jobData.sequence) {
      handleCreateNewExperiment(jobData.sequence);
    } else {
      Sentry.captureMessage(errorMessage, 'error');
      alertSys.setAlert(errorMessage);
    }

    // Remove job param from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('job');
    setSearchParams(newParams, { replace: true });
  }, [
    alertSys,
    handleCreateNewExperiment,
    jobData,
    searchParams,
    setSearchParams
  ]);

  function dragIsFile(e: DragEvent) {
    return e.dataTransfer?.types?.includes('Files') ?? false;
  }

  function dragOver(e: DragEvent) {
    if (dragIsFile(e)) {
      e.preventDefault();
    }
  }

  function dragEnter(e: DragEvent) {
    if (dragIsFile(e)) {
      e.preventDefault();
      setDragging(1);
    }
  }

  function dragLeave(e: DragEvent) {
    if (dragIsFile(e)) {
      setDragging(0);
    }
  }

  async function onDrop(e: DragEvent) {
    setDragging(0);
    if (!dragIsFile(e)) {
      return;
    }
    const data = e.dataTransfer;
    if (data) {
      if (data.files.length === 1) {
        e.preventDefault();
        e.stopPropagation();
        const file = data.files[0];
        const text = await file.text();
        tryLoad(text);
      } else if (data.items.length === 1) {
        e.preventDefault();
        e.stopPropagation();
        const item = data.items[0];
        if (item.kind === 'string') {
          item.getAsString((text) => {
            tryLoad(text);
          });
        }
      }
    }
  }

  return (
    <>
      <ContextMenu />
      <HelpWindow />
      <AlertContent />

      <div
        className={`ExperimentPage${dragging > 0 ? ' is-dragging' : ''}`}
        onDragEnter={dragEnter}
        onDragLeave={dragLeave}
        onDragOver={dragOver}
        onDrop={onDrop}
      >
        <div className="ExperimentPage-body">
          <Header appName="Pulser Studio" />
          <div className="ExperimentPage-main">
            <Sidebar
              logoutButton={
                <TitleIconButton
                  title="Log out"
                  icon="log-out"
                  label="Log out"
                  onClick={logout}
                />
              }
            >
              <SidebarContent
                isCreatingNewExperiment={isCreatingNewExperiment}
              />
            </Sidebar>
            <div className="ExperimentPage-center">
              <div className="ExperimentPage-header">
                <ExperimentTabs
                  isCreatingNewExperiment={isCreatingNewExperiment}
                  isSyncingState={isSyncingState}
                  onNewExperiment={(isNewExperimentTabOpen: boolean) => {
                    setIsCreatingNewExperiment(isNewExperimentTabOpen);
                  }}
                />
                {!isCreatingNewExperiment && <UndoControl />}
              </div>
              {isCreatingNewExperiment ? (
                <NewExperimentTab
                  onCreateSequence={handleCreateNewExperiment}
                />
              ) : (
                <LayoutRoot />
              )}
            </div>
            {!isCreatingNewExperiment && (
              <div className="ExperimentPage-rim ExperimentPage-rim--right">
                <ExperimentRunControl />
                <ExperimentSpecsControl />
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

function ExperimentPageProviders() {
  return (
    <ExperimentProvider>
      <SimulationProvider>
        <ExperimentPageWithCurrentFrame />
      </SimulationProvider>
    </ExperimentProvider>
  );
}

function ExperimentPageWithCurrentFrame() {
  const experiment = useExperiment();
  const simulation = useSimulation();
  return (
    <CurrentFrameProvider simulation={simulation} experiment={experiment}>
      <UiStateProvider>
        <ExperimentPageInner />
      </UiStateProvider>
    </CurrentFrameProvider>
  );
}

export default ExperimentPageProviders;
